import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  Container,
  styled,
  Typography,
} from "@mui/material";
import { ReactElement, useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import { palette } from "~frontend/theme";
import useMousetrap from "~frontend/util/useMousetrap";

const Disclaimer = styled("p")({
  margin: "50px auto 0",
  maxWidth: "400px",
  fontStyle: "italic",
  color: palette.darkGray,
});

export default function Login(): ReactElement {
  const urlParams = new URLSearchParams(window.location.search);
  const email = urlParams.get("inviteEmail");
  const defaultToSignup = urlParams.get("inviteHasAccount") === "false";
  const loginImmediately = urlParams.get("loginImmediately") === "true";
  const [staff, setStaff] = useState(urlParams.get("staff") === "true");

  const onClick = useCallback(async () => {
    const loginParams = new URLSearchParams({
      return_to: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      screen_hint: defaultToSignup ? "signup" : "login",
      login_hint: email || "",
    });

    document.location = `/auth/${staff ? "staff-login" : "login"}?${loginParams.toString()}`;
  }, [defaultToSignup, email, staff]);

  useEffect(() => {
    if (loginImmediately) {
      void onClick();
    }
  }, [onClick, loginImmediately]);

  useMousetrap("mod+shift+s", evt => {
    evt.preventDefault();
    setStaff(!staff);
  });

  return (
    <Container maxWidth="sm" sx={{ marginTop: 8 }}>
      <Card>
        <CardHeader
          sx={{ p: 2 }}
          title={
            <Typography variant="h2">
              <FormattedMessage
                defaultMessage="Log In"
                description="Header for login box"
              />
            </Typography>
          }
        />
        <CardContent>
          <FormattedMessage
            defaultMessage="Welcome to Scale to Win."
            description="Intro text to login box"
          />{" "}
          {defaultToSignup ? (
            <FormattedMessage
              defaultMessage="Sign up to get started."
              description="Intro text in login box for new users"
            />
          ) : (
            <FormattedMessage
              defaultMessage="Log in to get started."
              description="Intro text in login box for existing users"
            />
          )}
        </CardContent>
        <CardActions sx={{ p: 2 }}>
          <Button variant="contained" color="primary" onClick={onClick}>
            {staff ? (
              "Staff Login"
            ) : defaultToSignup ? (
              <FormattedMessage
                defaultMessage="Sign up"
                description="Button text to sign up for an account"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Log in"
                description="Button text to log in"
              />
            )}
          </Button>
        </CardActions>
      </Card>
      <Disclaimer>
        <FormattedMessage
          defaultMessage="By logging in, you agree to the Scale to Win {termsOfService} and {privacyPolicy}. Scale to Win may share your name, email, and phone number with this organization."
          description="Disclaimer text at the bottom of the login box"
          values={{
            termsOfService: (
              <a
                href="https://scaletowin.com/terms-platform/"
                target="_new"
                rel="noopener noreferrer"
              >
                <FormattedMessage
                  defaultMessage="terms of service"
                  description="Link text for the terms of service"
                />
              </a>
            ),
            privacyPolicy: (
              <a
                href="https://scaletowin.com/privacy-policy/"
                target="_new"
                rel="noopener noreferrer"
              >
                <FormattedMessage
                  defaultMessage="privacy policy"
                  description="Link text for the privacy policy"
                />
              </a>
            ),
          }}
        />
      </Disclaimer>
    </Container>
  );
}
